// packages/console/src/api/jobs.ts
//
// FT-3 orchestrator client: jobs, presets. Types mirror
// services/orchestrator/internal/jobs (Job, Preset, Rung) and the httpapi
// request bodies exactly. Note the real preset model differs from the
// prototype's illustrative one: container/videoCodec/rateControl are lowercase
// enums, gopLength is in FRAMES, speedPreset is p1..p7, and the ladder is a
// list of rungs. The console maps the prototype's controls onto these.

import { servicePaths } from "./config";
import { request, requestBlob } from "./http";

export type JobState = "queued" | "running" | "completed" | "failed";
export type ErrorClass = "validation" | "asset" | "decode" | "encode" | "internal";

// OutputFormat is one delivery format's stored artifact for a rung (mirrors
// services/orchestrator/internal/jobs OutputFormat). The player reads this to
// decide which rungs are HLS-capable (an `hls` format) for job-level ABR.
export interface OutputFormat {
  format: Container;
  objectKey: string;
}

export interface OutputProgress {
  name: string;
  objectKey?: string;
  // formats lists every delivery format produced from the one encode (encode
  // once, transmux many); absent on older jobs. A rung is HLS-capable when it
  // has an `hls` entry here.
  formats?: OutputFormat[];
  state: string;
  progressPct: number;
}

export interface Job {
  id: string;
  workspaceId: string;
  userId: string;
  presetId: string;
  sourceObjectKey: string;
  sourceSha256: string;
  // Original uploaded filename, shown as the human job name. Absent for jobs
  // created before filename propagation (fall back to the object-key basename).
  sourceFilename?: string;
  // posterObjectKey is set once the FT-3 scene-aware poster has been generated
  // and uploaded for the job; absent until then (server omits it when empty).
  posterObjectKey?: string;
  state: JobState;
  errorClass?: ErrorClass;
  errorMessage?: string;
  attempts: number;
  progressPct: number;
  fps: number;
  speedX: number;
  etaSeconds: number;
  outputs: OutputProgress[];
  createdAt: string;
  queuedAt: string;
  startedAt?: string;
  finishedAt?: string;
  updatedAt: string;
}

export type Container = "mp4" | "mov" | "hls" | "dash" | "cmaf" | "webm";
export type VideoCodec = "h264" | "hevc" | "av1" | "vp8" | "vp9" | "svtav1" | "libaom";
export type RateControl = "crf" | "vbr" | "cbr";
export type SpeedPreset = "p1" | "p2" | "p3" | "p4" | "p5" | "p6" | "p7";
// Scaling picks how the source frame is fit into the rung dimensions: fit pads
// (letterbox, preserve aspect), fill crops the overflow, stretch forces the
// exact rung size (distorts on mismatch). Preset-global (shared by every rung).
export type Scaling = "fit" | "fill" | "stretch";
// AudioCodec is the concrete audio encoder; "" = container-derived default.
export type AudioCodec = "aac" | "opus" | "";

export interface Rung {
  name: string;
  width: number;
  height: number;
  // Per-rung target bitrate (kbps) for vbr/cbr; 0/absent = use the preset bitrate.
  bitrateKbps?: number;
  // Per-rung codec override; absent = use the preset codec. Always an override.
  videoCodec?: VideoCodec;
  // Per-rung output fps, applied only when the preset has perRungFrameRate set
  // (0/absent = source cadence for this rung).
  frameRate?: number;
  // Per-rung audio bitrate (kbps), applied only when the preset has perRungAudio
  // set (0/absent = codec default for this rung).
  audioBitrateKbps?: number;
  // Codec profile id (codec-dependent; see PROFILE_MAP in lib/preset).
  // ""/absent = encoder default.
  profile?: string;
  // Encode level; "auto"/""/absent = auto. Only meaningful for h264/hevc.
  level?: string;
  // Sharpen/smooth filter: <0 sharpen, >0 smooth, 0 none. Range -10..10.
  filter?: number;
  // Per-rung audio codec; "" = container-derived default.
  audioCodec?: AudioCodec;
  // Per-rung audio channel count: 0 source, 1 mono, 2 stereo.
  audioChannels?: number;
}

export interface Preset {
  id: string;
  workspaceId: string;
  name: string;
  container: Container; // primary/first delivery format
  formats?: Container[]; // full delivery set (encode once, transmux many)
  videoCodec: VideoCodec;
  rateControl: RateControl;
  crf?: number;
  bitrateKbps?: number;
  maxBitrateKbps?: number;
  gopLength: number;
  // bFrames is the consecutive B-frame count between reference frames (0..4).
  // It is a LIVE-preset parameter (the live encoder's Rendition panel drives it);
  // absent/0 on file presets, which do not expose it. 0 = no B-frames.
  bFrames?: number;
  frameRate?: number; // output fps; 0/absent = source cadence
  audioBitrateKbps?: number; // 0/absent = codec default
  audioChannels?: number; // 0 = source, 1 mono, 2 stereo
  audioDisabled?: boolean; // strip audio
  perRungFrameRate?: boolean; // true = frame rate is set per rung, else preset-wide
  perRungAudio?: boolean; // true = audio bitrate is set per rung, else preset-wide
  speedPreset: SpeedPreset;
  // Force-keyframe cadence in seconds; 0 = use gopLength frames only. 0..10.
  keyframeSeconds?: number;
  // HLS/DASH segment target seconds; 0 = default 6. 1..30.
  segmentSeconds?: number;
  // Scene-cut detection on/off (default on when absent).
  sceneCut?: boolean;
  // How the source is fit into each rung (preset-global). Default "fit".
  scaling?: Scaling;
  // 8 = yuv420p (web-safe), 10 = yuv420p10le (web-unsafe inline). Default 8.
  bitDepth?: number;
  // VBR only: maxrate = ratio × target bitrate; 0 = unset. 1.0..4.0.
  vbvMaxRatio?: number;
  // VBR only: bufsize = ratio × target bitrate; 0 = unset. 1.0..8.0.
  vbvBufRatio?: number;
  // Default audio codec that SEEDS new rungs; "" = container-derived.
  audioCodec?: AudioCodec;
  // eve marks a managed EVE profile (seeded from the delivery×codec matrix).
  // The profile picker filters on it (EVE-on shows only eve=true profiles) and
  // the editor exposes only the restricted EVE selector set for such presets.
  eve?: boolean;
  // mode partitions a preset by console surface: "file" presets encode uploaded
  // VOD, "live" presets drive a live ABR ladder. Absent = "file" (the file
  // console's presets predate this field, so an unset value reads as file).
  // The live console lists only mode==="live" presets; the file console lists
  // the rest. Its Rendition panel PATCHes the selected live preset.
  mode?: "file" | "live";
  // builtin marks a system-provided profile: the Standard set and the seeded
  // default. The picker groups builtin=true under "Standard" and the rest under
  // "Custom". It carries no encode semantics.
  builtin?: boolean;
  // createdBy is the user id that created a CUSTOM profile; absent for a system
  // profile (EVE/standard). Drives ownership: the creator (and the workspace owner)
  // may see/edit it; other users never see it. See lib/roles.canEditPreset.
  createdBy?: string;
  // cmdOverride is the admin editable ffmpeg command (Phase 3, EVE full view).
  // Empty/absent = the engine builds the command from the structured fields
  // (today's behavior). It is OWNER-gated end to end: the server only returns it
  // to workspace owners and only accepts writes from owners. It is argv, not a
  // shell script (the server tokenizes it by shell-word split, no shell).
  cmdOverride?: string;
  // Live pre-clean toggle (mode==="live" only): a software deband pass run on the
  // source before scale, so a low-bitrate live encode is not asked to spend bits on
  // banding. Absent = off.
  liveDeband?: boolean;
  // liveCq (mode==="live" only): the nvenc constant-quality target (0..51; lower =
  // higher quality) for the quality rate controls (vbr/crf); ignored for cbr. Absent
  // = encoder default.
  liveCq?: number;
  // liveSourceBlur (mode==="live", ADMIN-ONLY): gaussian sigma applied to the source
  // before the downscale (0 = off). The server returns 0 to non-admins and rejects a
  // non-admin write, so this is only meaningful in an admin/owner session.
  liveSourceBlur?: number;
  ladder: Rung[];
  createdAt: string;
  updatedAt: string;
}

// PresetPatch carries only the fields being changed (PATCH semantics). The
// server validates the merged result, so a rateControl change must arrive with
// its consistent value fields.
export interface PresetPatch {
  name?: string;
  container?: Container;
  formats?: Container[];
  videoCodec?: VideoCodec;
  rateControl?: RateControl;
  crf?: number;
  bitrateKbps?: number;
  maxBitrateKbps?: number;
  gopLength?: number;
  bFrames?: number;
  frameRate?: number;
  audioBitrateKbps?: number;
  audioChannels?: number;
  audioDisabled?: boolean;
  perRungFrameRate?: boolean;
  perRungAudio?: boolean;
  speedPreset?: SpeedPreset;
  keyframeSeconds?: number;
  segmentSeconds?: number;
  sceneCut?: boolean;
  scaling?: Scaling;
  bitDepth?: number;
  vbvMaxRatio?: number;
  vbvBufRatio?: number;
  audioCodec?: AudioCodec;
  eve?: boolean;
  mode?: "file" | "live";
  // Admin editable ffmpeg command override (Phase 3). Owner-gated: the server
  // rejects a non-owner who sends this field.
  cmdOverride?: string;
  liveDeband?: boolean;
  liveCq?: number;
  liveSourceBlur?: number;
  ladder?: Rung[];
}

export interface CreateJobRequest {
  objectKey: string;
  presetId: string;
  // formats is the per-job packaging override (the inline Packaging select).
  // Absent/empty = use the preset's formats (today's behavior). When set, each
  // entry must be a container the preset's codec can carry (the server 422s a
  // bad combo); the set replaces the preset's delivery formats for THIS job.
  formats?: string[];
  // destinationIds binds delivery destinations to this job (Phase 1). Absent/
  // empty = no external destination (local library only). The server 404s the
  // whole delivery surface when delivery is not enabled.
  destinationIds?: string[];
}

const j = servicePaths.jobs;

export function listJobs(state?: JobState, signal?: AbortSignal): Promise<{ jobs: Job[] }> {
  const q = state ? `?state=${encodeURIComponent(state)}` : "";
  return request<{ jobs: Job[] }>(`${j}/v1/jobs${q}`, { signal });
}

export function getJob(id: string, signal?: AbortSignal): Promise<Job> {
  return request<Job>(`${j}/v1/jobs/${encodeURIComponent(id)}`, { signal });
}

export function createJob(req: CreateJobRequest): Promise<Job> {
  return request<Job>(`${j}/v1/jobs`, { method: "POST", json: req });
}

// fetchPoster fetches the FT-3 auto-generated scene-aware poster JPEG for a job
// (GET /v1/jobs/{id}/poster, workspace-scoped by the same bearer auth as every
// other route) and returns an object URL for it. It returns null when the job
// has no poster yet (the route 404s); any other error propagates so the caller
// SourceMediaInfo mirrors the orchestrator probe (engine.MediaInfo): the
// container, codecs, resolution, chroma, bitrate, duration, and stream counts
// of the uploaded source.
export interface SourceMediaInfo {
  container: string;
  durationSeconds: number;
  videoCodec?: string;
  audioCodec?: string;
  width?: number;
  height?: number;
  chromaSubsampling?: string;
  sourceBitrateBps?: number;
  videoStreams: number;
  audioStreams: number;
  subtitleStreams: number;
}

// fetchSourceMediaInfo returns the probe media info for a job's source, or null
// when the source has not been probed yet (404) so the caller shows an honest
// empty state rather than fabricating values (R10).
export async function fetchSourceMediaInfo(id: string, signal?: AbortSignal): Promise<SourceMediaInfo | null> {
  try {
    return await request<SourceMediaInfo>(`${j}/v1/jobs/${encodeURIComponent(id)}/source`, { signal });
  } catch (err) {
    if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
      return null;
    }
    throw err;
  }
}

export interface OutputUrl {
  rung: string;
  url: string;
  expiresInSeconds: number;
}

// fetchOutputUrl asks the orchestrator for a short-lived presigned URL to a
// completed ladder output, so the browser can stream/download the encoded file
// directly from object storage. The rung is matched server-side against the
// job's recorded outputs; the server only presigns a stored key.
//
// format selects WHICH of the rung's delivery formats to presign (server
// resolves it against the rung's formats[]); absent, the server presigns the
// rung's primary artifact. This is what lets the player offer every produced
// progressive format and serve the RIGHT file for each (not just the primary).
export function fetchOutputUrl(
  id: string,
  rung: string,
  opts?: { format?: Container; download?: boolean; signal?: AbortSignal },
): Promise<OutputUrl> {
  // download=1 asks the server to presign with an attachment Content-Disposition
  // so the browser saves the file instead of playing it inline; format= threads
  // the selected delivery format so the server presigns that format's own key.
  const params = new URLSearchParams();
  if (opts?.format) params.set("format", opts.format);
  if (opts?.download) params.set("download", "1");
  const q = params.toString() ? `?${params.toString()}` : "";
  return request<OutputUrl>(
    `${j}/v1/jobs/${encodeURIComponent(id)}/outputs/${encodeURIComponent(rung)}${q}`,
    { signal: opts?.signal },
  );
}

// DownloadAllUrl is the short-lived, token-carrying URL for downloading a job's
// ENTIRE output stack as a zip (Martha #9). The url is already absolute to the
// jobs service (base + the server's token-carrying path), so the browser can
// navigate straight to it; expiresInSeconds bounds its validity.
export interface DownloadAllUrl {
  url: string;
  expiresInSeconds: number;
}

// fetchDownloadAllUrl asks the orchestrator (bearer-authed) to mint a short-lived
// signed URL that downloads the whole output stack of a completed job as a zip
// (every rung, every format, the job master, and the poster). Because a browser
// download cannot carry an Authorization header, the download itself is
// authenticated by a token in this URL, not the bearer. The server returns a
// jobs-service-relative path ("/v1/jobs/…/download-all.zip?token=…"); we prefix
// the jobs service base so the returned url is navigable as-is.
export async function fetchDownloadAllUrl(id: string, signal?: AbortSignal): Promise<DownloadAllUrl> {
  const res = await request<DownloadAllUrl>(
    `${j}/v1/jobs/${encodeURIComponent(id)}/download-all-url`,
    { signal },
  );
  return { url: `${j}${res.url}`, expiresInSeconds: res.expiresInSeconds };
}

// can render an honest failure rather than a fabricated frame. The caller owns
// the returned object URL and must URL.revokeObjectURL it when done.
export async function fetchPoster(id: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const blob = await requestBlob(`${j}/v1/jobs/${encodeURIComponent(id)}/poster`, { signal });
    return URL.createObjectURL(blob);
  } catch (err) {
    if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
      return null;
    }
    throw err;
  }
}

export function retryJob(id: string): Promise<Job> {
  return request<Job>(`${j}/v1/jobs/${encodeURIComponent(id)}/retry`, { method: "POST" });
}

export function cancelJob(id: string): Promise<unknown> {
  return request<unknown>(`${j}/v1/jobs/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function listPresets(signal?: AbortSignal): Promise<{ presets: Preset[] }> {
  return request<{ presets: Preset[] }>(`${j}/v1/presets`, { signal });
}

// NewPreset is the full preset definition sent to POST /v1/presets (the server
// assigns id/workspaceId/timestamps).
export type NewPreset = Omit<Preset, "id" | "workspaceId" | "createdAt" | "updatedAt">;

export function createPreset(p: NewPreset): Promise<Preset> {
  return request<Preset>(`${j}/v1/presets`, { method: "POST", json: p });
}

// deletePreset removes a preset. The server refuses (409) to delete the
// workspace's only preset or one still referenced by a job.
export function deletePreset(id: string): Promise<unknown> {
  return request<unknown>(`${j}/v1/presets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function getPreset(id: string, signal?: AbortSignal): Promise<Preset> {
  return request<Preset>(`${j}/v1/presets/${encodeURIComponent(id)}`, { signal });
}

// RungCommand is the exact ffmpeg argv the engine would run for one rung: an
// argv ARRAY (never a shell string), with {{input}}/{{output}}/{{width}}/… tokens
// marking the per-rung values the engine substitutes at encode time.
export interface RungCommand {
  rung: string;
  argv: string[];
}

// PresetCommand is the generated-command view for a preset (owner-only). It
// carries the current cmdOverride (empty = structured build) and, per rung, the
// argv the engine would run so the admin sees the real starting point.
export interface PresetCommand {
  presetId: string;
  cmdOverride: string;
  rungs: RungCommand[];
}

// getPresetCommand fetches the generated ffmpeg command for a preset
// (GET /v1/presets/{id}/command). The server owner-gates it: a non-owner gets
// 403. It is the read side of the Phase 3 EVE full view.
export function getPresetCommand(id: string, signal?: AbortSignal): Promise<PresetCommand> {
  return request<PresetCommand>(`${j}/v1/presets/${encodeURIComponent(id)}/command`, { signal });
}

export function patchPreset(id: string, patch: PresetPatch): Promise<Preset> {
  return request<Preset>(`${j}/v1/presets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    json: patch,
  });
}

// ProbeGraph is the admin-only probe bit-distribution graph for a job: the
// binned bits/frame envelope over time, the detected cut points, the CABA
// bits/sec target track, and the per-scene banding assessment. The field
// names/types mirror the orchestrator's agreed probe-graph response schema
// exactly; any drift with the backend's final schema is an integration note.
export interface ProbeGraph {
  jobId: string;
  schemaVersion: number;
  meta: {
    durationSeconds: number;
    totalFrames: number;
    fps: number;
    secPerFrame: number;
    probeParams: {
      codec: string;
      preset: string;
      cq: number;
      analysisWidth: number;
      analysisHeight: number;
    };
    computedAt: string;
    cached: boolean;
  };
  bins: {
    count: number;
    t: number[];
    bitsMax: number[];
    bitsMean: number[];
    // bitsByType is present only when the graph was fetched with frameTypes=1;
    // the panel's I/P/B toggle drives that fetch, so an untoggled fetch omits it.
    bitsByType?: { I: number[]; P: number[]; B: number[] };
  };
  cuts: { seconds: number; kind: string; source: string }[];
  cabaTarget: {
    steps: { startSeconds: number; endSeconds: number; targetBitsPerSec: number }[];
    note: string;
  };
  banding: {
    wholeFileLumaLsb: number;
    scenes: { startSeconds: number; endSeconds: number; lumaLsb: number; debandWouldFire: boolean }[];
  };
}

// getJobProbeGraph fetches the admin-only probe bit-distribution graph for a
// job (GET /v1/jobs/{id}/probe-graph). The query threads the panel's controls:
//   maxPoints  - cap the number of binned points the server returns
//   frameTypes - request the per-type (I/P/B) breakdown (the panel's toggle)
//   refresh    - force a recompute rather than serving the cached graph
// The thrown ApiError carries .status, so the panel maps 403 (forbidden, not an
// admin) and 429 (busy, the probe graph is being computed) to distinct states
// and never fabricates a graph on failure (R10).
export function getJobProbeGraph(
  id: string,
  opts?: { maxPoints?: number; frameTypes?: boolean; refresh?: boolean },
  signal?: AbortSignal,
): Promise<ProbeGraph> {
  const params = new URLSearchParams();
  if (opts?.maxPoints !== undefined) params.set("maxPoints", String(opts.maxPoints));
  if (opts?.frameTypes) params.set("frameTypes", "1");
  if (opts?.refresh) params.set("refresh", "1");
  const q = params.toString() ? `?${params.toString()}` : "";
  return request<ProbeGraph>(`${j}/v1/jobs/${encodeURIComponent(id)}/probe-graph${q}`, { signal });
}
